import angular from "angular";

import { DateRange, Dates, Translations } from "@salaxy/core";
import AirDatepicker from "air-datepicker";
import localeEn from "air-datepicker/locale/en";
import localeFi from "air-datepicker/locale/fi";
import localeSv from "air-datepicker/locale/sv";

/**
 * Provides a user interface for picking up a date range
 * and optionally specifying also the number of working days within that range.
 */
export class DateRangeController implements angular.IController {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["$element", "$timeout"];

  /** Label for the control */
  public label: string;

  /** Label for the scondary input. Translation is attempted. Default is SALAXY.NG1.DateRange.labelDaysCount.  */
  public labelDaysCount: string;

  /** The model that is bound to the input */
  public model: angular.INgModelController;

  /** The datepicker element instance. */
  private datepicker: any;

  /**
   * The selection mode is either:
   * - "range" for (default) calendar control with start and end.
   * - "calc" for period selection that is optimized for salary calculation (months, 2 weeks, 1/2 months etc.)
   */
  public mode: "range" | "calc";

  /** Form controller, if available */
  public form: angular.IFormController;

  /** If there is a validation error in period start, it is added here. */
  public periodStartDateError = null;

  /**
   * If true, displays the control as read-only div instead of the input control.
   * This "read-only" attribute is visualized in a different way than standard html "readonly" (ng-readonly):
   * The input is not shown and display is more compact.
   * Also note, that you may change the same read-only for the form using readonly / ng-readonly attribute.
   * @example
   * ```html
   * <form name="foo" ng-readonly="true">
   * <!-- .... or plain HTML ... -->
   * <form name="foo" readonly="true">
   * <!-- .... or plain HTML ... -->
   * <salaxy-input name="input1" read-only="true"></salaxy-input>
   */
  public readOnly: boolean;

  /**
   * Fires an event when the model is changing: Any of the values specific to the model are changing: start, end or daysCount.
   * This should typically used instead of ng-change because ng-change will only fire if the object reference changes.
   * On-change fires when dates or day count changes and this is typically what you are looking for.
   * @example <salaxy-date-range ng-model="$ctrl.dateRange"></salaxy-date-range>
   */
  public onChange: () => void;

  /** Validation error text */
  public validationError: string;

  /** User interface shortcuts for period selection */
  public periodShortcuts: {
    /** Text for current month. */
    monthCurrent: string,
    /** Text for last month. */
    monthLast: string,
    /** Text for other month. */
    monthOther: string,
    // monthMulti: "Monta kuukautta",
    /** Text for two weeks. */
    weeks2: string,
    /** Text for half a month */
    monthHalf: string,
    /** Text for other period. */
    other: string,
  };

  /** Form control name */
  public name: string;

  /** If true, the input value is required */
  public require: boolean;

  /** Value, that is used when selecting "other" period selection by typing a manual start date. */
  private _periodStartDate: string | null;

  /** Object containing all the values in the date range selection (start, end, days and daysCount). */
  public dateRangeValues: DateRange & { formattedRange?: string };

  /** Formatted start date displayed in the input field */
  private _valueStart: string | null;

  /** Formatted end date displayed in the input field */
  private _valueEnd: string | null;

  /** User's manual input for start date */
  private _manualInputStart: string | null;

  /** User's manual input for end date */
  private _manualInputEnd: string | null;

  /** Getter and setter for the start date value for the input field */
  public get datepickerValueStart(): string {
    if (this._manualInputStart != null) {
      return this._manualInputStart;
    }

    return this._valueStart;
  }
  public set datepickerValueStart(value: string) {
    this._manualInputStart = value;
  }

  /** Getter and setter for the end date value for the input field */
  public get datepickerValueEnd(): string {
    if (this._manualInputEnd != null) {
      return this._manualInputEnd;
    }

    return this._valueEnd;
  }
  public set datepickerValueEnd(value: string) {
    this._manualInputEnd = value;
  }

  constructor(private $element: JQLite, private $timeout: angular.ITimeoutService) {}

  /** Set the values and defaults on init */
  public $onInit() {
    this.dateRangeValues = {
      start: null,
      end: null,
      days: [],
      daysCount: null
    };

    const currentMonth = Dates.getMonth(Dates.getToday());
    this.periodShortcuts = {
      monthCurrent: Translations.get(`SALAXY.ENUM.CalendarMonth.month${currentMonth}.label`),
      monthLast: Translations.get(`SALAXY.ENUM.CalendarMonth.month${currentMonth === 1 ? 12 : (currentMonth - 1)}.label`),
      monthOther: Translations.get("SALAXY.NG1.DateRange.otherMonth"),
      // monthMulti: "Monta kuukautta",
      weeks2: Translations.get("SALAXY.UI_Terms.week2"),
      monthHalf: Translations.get("SALAXY.UI_Terms.monthHalf"),
      other: Translations.get("SALAXY.UI_Terms.otherPeriod"),
    };

    this.model.$render = () => {
      if (this.model.$viewValue?.start && this.model.$viewValue?.end) {
        this._valueStart = Dates.getFormattedDate(this.model.$viewValue.start);
        this._valueEnd = Dates.getFormattedDate(this.model.$viewValue.end);
        this._periodStartDate = Dates.format(this.model.$viewValue.start, "D.M.", null);
        this.dateRangeValues = this.buildDateRangeValues(this.model.$viewValue);
      }
    };

    this.initDatepicker();
  }

  /** Gets or sets the period start date */
  public get periodStartDate(): string {
    return this._periodStartDate;
  }
  public set periodStartDate(value: string) {
    this._periodStartDate = value;
    const parts = (value || "").split(".");
    if (parts.length < 2 || parts.length > 3) {
      this.periodStartDateError = Translations.get("SALAXY.NG1.DateRange.periodStartDateError");
      return;
    }
    let year = (parts.length === 3) ? Number((parts[2] || "").trim()) : null;
    if (!year || year < 2019 || year > 2100) {
      year = Number(this.dateRangeValues.start.substr(0, 4));
    }
    const date = Dates.getDate(year, Number(parts[1]), Number(parts[0]));
    if (date) {
      this.periodStartDateError = null;
      switch (this.periodShortcut) {
        case "monthCurrent":
        case "monthLast":
        case "monthOther":
          this.dateRangeValues = Dates.getDateRange(date, Dates.add(Dates.add(date,1,"month"),-1, "day"));
          break;
        case "weeks2":
          this.dateRangeValues = Dates.getDateRange(date, Dates.add(Dates.add(date,2,"week"),-1, "day"));
          break;
        case "monthHalf":
          if (Dates.getDay(date) === 15 || Dates.getDay(date) === 16 ) {
            this.dateRangeValues = Dates.getDateRange(date, Dates.startEnd(date,"end-month"));
          } else {
            this.dateRangeValues = Dates.getDateRange(date, Dates.add(date,14, "day"));
          }
          break;
        case "other":
          this.dateRangeValues = Dates.getDateRange(date, date);
          break;
    }
      this.datepicker.clear({silent: true});
      this.setValues(this.dateRangeValues, false);
    } else {
      this.periodStartDateError = `${value}` + Translations.get("SALAXY.NG1.DateRange.periodStartDateError2");
    }
  }

  /** Gets the user interface shortcut for the period. */
  public get periodShortcut(): string {
    if (!this.dateRangeValues || !this.dateRangeValues.start || !this.dateRangeValues.end) {
      return null;
    }
    if (this.dateRangeValues.start === Dates.getDate("today", "today", 1)
      && this.dateRangeValues.end === Dates.startEnd("today","end-month")) {

        return "monthCurrent";
    }
    if (this.dateRangeValues.start === Dates.add(Dates.startEnd("today","start-month"),-1, "month")
      && this.dateRangeValues.end === Dates.startEnd(Dates.add(Dates.startEnd("today","start-month"),-1, "month"),"end-month")) {
      return "monthLast";
    }
    if (this.dateRangeValues.end === Dates.add(Dates.add(this.dateRangeValues.start,1, "month"),-1, "day")) {
      return "monthOther";
    }
    if (this.dateRangeValues.end === Dates.add(Dates.add(this.dateRangeValues.start,2, "week"),-1, "day")) {
      return "weeks2";

    }

    // if the range is 15 (14) days or
    // starts 15th or 16th and ends month end
    if ((Dates.getDuration(this.dateRangeValues.start, this.dateRangeValues.end ).days() === 14)
      || (Dates.getDay(this.dateRangeValues.start) === 15 && this.dateRangeValues.end === Dates.startEnd(this.dateRangeValues.start,"end-month"))
      || (Dates.getDay(this.dateRangeValues.start) === 16 && this.dateRangeValues.end === Dates.startEnd(this.dateRangeValues.start,"end-month"))) {
        return "monthHalf";
    }

    // TODO: Potentially add other options.
    return "other";
  }
  public set periodShortcut(value: string) {
    this.periodStartDateError = null;
    switch (value) {
      case "monthCurrent":
        this.dateRangeValues.start = Dates.startEnd("today","start-month");
        this.dateRangeValues.end = Dates.startEnd("today","end-month");
        break;
      case "monthOther":
        if (Dates.getDay("today") === 1) {
          // Move to tomorrow so that the selection is monthOther and not monthCurrent.
          this.dateRangeValues.start = Dates.add("today",1,"day");
        } else {
          this.dateRangeValues.start = Dates.asDate("today");
        }
        this.dateRangeValues.end = Dates.add(Dates.add(this.dateRangeValues.start,1,"month"),-1,"day");
        break;
      case "monthLast":
        this.dateRangeValues.start = Dates.add(Dates.startEnd("today","start-month"),-1, "month");
        this.dateRangeValues.end = Dates.startEnd(this.dateRangeValues.start,"end-month");
        break;
      case "weeks2":
        this.dateRangeValues.start = Dates.add(Dates.startEnd("today","start-week"),-2,"week"); //shift to start of current week, and then 2 weeks before
        this.dateRangeValues.end = Dates.add(Dates.startEnd("today","start-week"),-1,"day"); //shift to last day of previous week.
        break;
      case "monthHalf":

        // TODO Check this
        if (Dates.getDay("today") < 16) {
          // 16 - (28,29,30,31)
          this.dateRangeValues.start = Dates.add(Dates.add(Dates.startEnd("today", "start-month"),-1,"month"),15,"day");
          this.dateRangeValues.end = Dates.startEnd(this.dateRangeValues.start,"end-month");
        } else {
          // 1 - 15
          this.dateRangeValues.start = Dates.startEnd("today","start-month");
          this.dateRangeValues.end = Dates.add(this.dateRangeValues.start,14,"day");
        }
        break;
      case "other":
        this.dateRangeValues.start = Dates.add("today",-1,"day");
        this.dateRangeValues.end = Dates.asDate("today");
        break;
    }
    this.dateRangeValues = Dates.getDateRange(this.dateRangeValues.start, this.dateRangeValues.end);
    this._periodStartDate = Dates.format(this.dateRangeValues.start, "D.M.", null);
    this.setValues(this.dateRangeValues, true);
  }

  /**
   * Initialize the datepicker element.
   */
  public initDatepicker() {
    this.$timeout(() => {
      const dateRangeElem = this.$element[0].querySelector(".input-daterange") as HTMLElement;
      if (!dateRangeElem) {
        return;
      }

      const localeMap = {
        fi: localeFi,
        en: localeEn,
        sv: localeSv
      };

      const lang = Translations.getLanguage();

      this.datepicker = new AirDatepicker(dateRangeElem, {
        locale: localeMap?.[lang] || localeFi,
        autoClose: true,
        range: true,
        toggleSelected: false,
        onSelect: (event) => {
          if (Array.isArray(event.date) && event.date.length > 1) {
            this.onSelect(event.date);
          }
        },
      });

      if (this.dateRangeValues?.start && this.dateRangeValues?.end) {
        this.datepicker.selectDate([this.dateRangeValues.start, this.dateRangeValues.end], { silent: true });
      }
    });
  }

  /**
   * Builds a complete DateRange object with formattedRange.
   * @param dateRange Partial or complete DateRange object
   * @returns Complete DateRange object with formattedRange
   */
  private buildDateRangeValues(dateRange: DateRange): any {
    return {
      ...dateRange,
      formattedRange: Dates.getFormattedRange(dateRange.start, dateRange.end)
    };
  }

  /**
   * Sets the current date range values and updates view/model as needed.
   * @param dateRangeVals The date range values to set (should contain start and end).
   * @param updateDatepickerElemValues If true, updates the datepicker element to reflect new values.
   */
  private setValues(dateRangeVals: DateRange, updateDatepickerElemValues: boolean) {
    if (dateRangeVals?.start) {
      if (updateDatepickerElemValues) {
        this.datepicker.selectDate([dateRangeVals.start, dateRangeVals.end], { silent: true });
      }
      this.dateRangeValues = this.buildDateRangeValues(dateRangeVals);
      this.model.$setViewValue(this.dateRangeValues);
      this._periodStartDate = Dates.format(this.dateRangeValues.start, "D.M.", null);
      this._valueStart = Dates.getFormattedDate(this.dateRangeValues.start);
      this._valueEnd = Dates.getFormattedDate(this.dateRangeValues.end);
      this._manualInputStart = null;
      this._manualInputEnd = null;
    }
  }

  /**
   * Datepicker element change event method.
   * @param date Date values that are picked in the datepicker.
   */
  private onSelect(date: any) {
    this.$timeout(() => {
      const start = Dates.asDate(date[0]);
      const end = Dates.asDate(date[1]);

      const dateObj = {
        start: start,
        end: end,
        daysCount: Dates.getWorkdays(start, end).length
      };

      this.validationError = "";
      this.setValues(dateObj, false);

      if (this.onChange) {
        this.onChange();
      }
    });
  }

  /** Gets the readonly value either from the controller or from the form */
  protected getReadOnly(): boolean {
    // This readOnly can be true, false or undefined
    const readOnlyValue = (this.readOnly + "").trim().toLowerCase();
    if (readOnlyValue === "true" || readOnlyValue === "1") {
      return true;
    }
    if (readOnlyValue === "false" || readOnlyValue === "0") {
      return false;
    }
    const readOnlyFormValue = !!this?.form?.$$element[0]?.attributes?.readonly;
    if (readOnlyFormValue) {
      return true;
    }

    return false;
  }

  /**
   * Normalizes date format by padding day and month with leading zeros.
   * @param value The date string to normalize (e.g., "1.2.2024")
   * @returns Normalized date string (e.g., "01.02.2024")
   */
  private normalizeDate(value: string): string {
    const parts = value.split(".");
    if (parts.length == 3) {
      return `${parts[0].padStart(2, "0")}.${parts[1].padStart(2, "0")}.${parts[2]}`;
    }
    return value;
  }

  /**
   * User types either the start or end date manually.
   */
  public changeManualDate() {
    this.validationError = "";
    const typedValueStart = (this._manualInputStart != null ? this._manualInputStart : this._valueStart);
    const typedValueEnd = (this._manualInputEnd != null ? this._manualInputEnd : this._valueEnd);

    if (!typedValueStart || !typedValueEnd) {
      return this.setError("SALAXY.NG1.DatepickerComponent.validationErrorDateRangeRequired");
    }

    const normalizedStart = this.normalizeDate(typedValueStart);
    const normalizedEnd = this.normalizeDate(typedValueEnd);

    const parsedStart = Dates.parseDate(normalizedStart, "DD.MM.YYYY");
    const parsedEnd = Dates.parseDate(normalizedEnd, "DD.MM.YYYY");

    if (!parsedStart || !parsedEnd) {
      return this.setError("SALAXY.NG1.DatepickerComponent.validationErrorDefault");
    }

    if (parsedEnd < parsedStart) {
      return this.setError("SALAXY.NG1.DatepickerComponent.validationErrorDateRangeInvalid");
    }

    // Get days' count, formatted range and update the date-range-picker below and its current view.
    const dateObj = {
      start: parsedStart,
      end: parsedEnd,
      daysCount: Dates.getWorkdays(parsedStart, parsedEnd).length
    };

    this.setValues(dateObj, true);
    this.datepicker.setViewDate(this.dateRangeValues.end);

    if (this.onChange) {
      this.onChange();
    }
  }

  /**
   * Sets the error message and clears the date range values.
   * @param errorMessage The error message to set.
   */
  private setError(errorMessage: string) {
    this.validationError = errorMessage;
    this.model.$setViewValue(null);
    this.datepicker.clear({ silent: true });
    return;
  }

  /**
   * Called when the daysCount input is manually changed.
   */
  public onDaysCountChange(): void {
    this.model.$setViewValue({ ...this.dateRangeValues });
    if (this.onChange) {
      this.onChange();
    }
  }
}